'use client'
import Image from 'next/image'
import LinkComponent from '@components/LinkComponent'
import { sendGAEvent } from '@next/third-parties/google'

// ─────────────────────────────────────────────────────────────────────────────
// BUILT-IN TRANSLATIONS  (no external dictionary needed)
// All 13 locales. formatFieldValue falls back to these when dictionary is absent.
// ─────────────────────────────────────────────────────────────────────────────
const PROJECT_DICT = {
  en: { newDevelopment: 'New Development', project: 'Project', highrise: 'Highrise',     lowrise: 'Lowrise',     presale: 'Pre-sale',    startingFrom: 'From',  developer: 'Developer' },
  th: { newDevelopment: 'โครงการใหม่',      project: 'โครงการ', highrise: 'ไฮไรส์',       lowrise: 'โลว์ไรส์',    presale: 'พรีเซล',      startingFrom: 'เริ่ม', developer: 'ผู้พัฒนา'  },
  cn: { newDevelopment: '新开发项目',          project: '项目',     highrise: '高层',          lowrise: '低层',         presale: '预售',         startingFrom: '起价',  developer: '开发商'      },
  ru: { newDevelopment: 'Новый проект',      project: 'Проект',  highrise: 'Высотный',     lowrise: 'Малоэтажный', presale: 'Предпродажа', startingFrom: 'От',    developer: 'Застройщик' },
  de: { newDevelopment: 'Neubauprojekt',     project: 'Projekt', highrise: 'Hochhaus',     lowrise: 'Flachbau',    presale: 'Vorverkauf',  startingFrom: 'Ab',    developer: 'Bauträger'  },
  fr: { newDevelopment: 'Nouveau projet',    project: 'Projet',  highrise: 'Tour',         lowrise: 'Bâtiment bas',presale: 'Prévente',    startingFrom: 'Dès',   developer: 'Promoteur'  },
  it: { newDevelopment: 'Nuovo progetto',    project: 'Progetto',highrise: 'Alto edificio', lowrise: 'Basso edificio', presale: 'Prevendita', startingFrom: 'Da',   developer: 'Promotore'  },
  es: { newDevelopment: 'Nuevo proyecto',    project: 'Proyecto',highrise: 'Rascacielos',  lowrise: 'Edificio bajo',presale: 'Preventa',    startingFrom: 'Desde', developer: 'Promotor'  },
  tr: { newDevelopment: 'Yeni proje',        project: 'Proje',   highrise: 'Yüksek bina',  lowrise: 'Alçak bina',  presale: 'Ön satış',    startingFrom: 'Başlangıç', developer: 'Geliştirici' },
  ja: { newDevelopment: '新規開発',            project: 'プロジェクト', highrise: '高層',       lowrise: '低層',         presale: '先行販売',     startingFrom: '～から', developer: 'デベロッパー' },
  ko: { newDevelopment: '신규 개발',           project: '프로젝트', highrise: '고층',          lowrise: '저층',         presale: '사전 분양',    startingFrom: '부터',  developer: '개발사'      },
  he: { newDevelopment: 'פרויקט חדש',        project: 'פרויקט',  highrise: 'בניין גבוה',   lowrise: 'בניין נמוך',  presale: 'מכירה מוקדמת', startingFrom: 'החל מ', developer: 'יזם'        },
  ar: { newDevelopment: 'مشروع جديد',        project: 'مشروع',   highrise: 'برج سكني',     lowrise: 'مبنى منخفض',  presale: 'بيع مسبق',    startingFrom: 'ابتداءً من', developer: 'المطوّر' },
}

// Returns the dict for `locale`, falling back key-by-key to English
function getDict(locale, external) {
  const base    = PROJECT_DICT.en
  const builtin = PROJECT_DICT[locale] ?? {}
  const ext     = external ?? {}
  // external dict wins → builtin locale → English fallback
  return new Proxy(base, {
    get(_, key) {
      return ext[key] ?? builtin[key] ?? base[key]
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const RATIO_CLASS = {
  '4/3':  'aspect-[4/3]',
  '3/2':  'aspect-[3/2]',
  '16/9': 'aspect-video',
  '1/1':  'aspect-square',
  '2/3':  'aspect-[2/3]',
}

const SHADOW_CLASS = {
  none: '',
  sm:   'shadow-sm',
  md:   'shadow-md',
  lg:   'shadow-lg',
}

function resolveColor(val, fallback = 'inherit') {
  if (!val) return fallback
  return val
}

function formatFieldValue(key, rawValue, d) {
  if (rawValue === undefined || rawValue === null || rawValue === false) return null
  switch (key) {
    case 'startingPrice':
    case 'minPrice':
    case 'maxPrice':
      return rawValue ? `฿ ${Number(rawValue).toLocaleString()}` : null
    case 'isHighrise':
      return rawValue ? d.highrise : null
    case 'isLowrise':
      return rawValue ? d.lowrise  : null
    case 'isPresale':
      return rawValue ? d.presale  : null
    case 'newDevelopment':
      return rawValue ? d.newDevelopment : d.project
    case 'developer':
      return rawValue ? String(rawValue) : null
    default:
      return rawValue ? String(rawValue) : null
  }
}

function resolveLabel(field, d, locale) {
  const override = field.labelOverride?.[locale] || field.labelOverride?.en
  if (override?.trim()) return override.trim()
  // built-in label fallbacks per key
  const AUTO = {
    developer:      d.developer,
    startingPrice:  d.startingFrom,
    minPrice:       d.startingFrom,
    maxPrice:       'Max',
    isHighrise:     d.highrise,
    isLowrise:      d.lowrise,
    isPresale:      d.presale,
    newDevelopment: d.newDevelopment,
  }
  return AUTO[field.key] ?? field.key
}

// ─────────────────────────────────────────────────────────────────────────────
// EXTRA FIELD RENDERER
// ─────────────────────────────────────────────────────────────────────────────
function ExtraField({ field, project, d, locale }) {
  const raw       = project[field.key]
  const formatted = formatFieldValue(field.key, raw, d)
  if (formatted === null || formatted === undefined) return null

  const labelText  = resolveLabel(field, d, locale)
  const valueStyle = { fontSize: field.valueSize ?? '0.875rem', fontWeight: field.valueWeight ?? '600', color: resolveColor(field.valueColor, 'inherit') }
  const labelStyle = { fontSize: field.labelSize ?? '0.75rem',  fontWeight: field.labelWeight ?? '400', color: resolveColor(field.labelColor, 'inherit'), whiteSpace: 'nowrap' }

  // Badge mode
  if (field.badgeMode === 'badge') {
    const badgeLabel = field.badgeTrueLabel?.trim() || formatted
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px',
        borderRadius: field.badgeRadius ?? '0.25rem',
        background:   resolveColor(field.badgeBg,    'var(--color-main)'),
        color:        resolveColor(field.badgeColor,  '#ffffff'),
        fontSize:     field.valueSize   ?? '0.875rem',
        fontWeight:   field.valueWeight ?? '600',
      }}>
        {field.icon ? `${field.icon} ` : ''}{badgeLabel}
      </span>
    )
  }

  // Column (stacked)
  if (field.direction === 'column') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {field.showLabel && <span style={labelStyle}>{labelText}:</span>}
      <span style={valueStyle}>{field.icon ? `${field.icon} ` : ''}{formatted}</span>
    </div>
  )

  // Inline row
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'baseline', flexWrap: 'wrap' }}>
      {field.showLabel && <span style={labelStyle}>{labelText}:</span>}
      <span style={valueStyle}>{field.icon ? `${field.icon} ` : ''}{formatted}</span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ProjectInfoCard
 *
 * Props:
 *   project    — linkedProject object from the server
 *   dictionary — optional external i18n dictionary (merged on top of built-ins)
 *   config     — composer config object (website.elements.project[cellId])
 *   locale     — current locale string, e.g. 'en', 'th', 'ar' (default: 'en')
 */
const ProjectInfoCard = ({ project, dictionary, config = {}, locale = 'en' }) => {
  if (!project) return null

  // Merged dictionary: external overrides built-in, English as final fallback
  const d = getDict(locale, dictionary)

  const {
    // image
    showImage       = true,
    imageRatio      = '4/3',
    imageRadius     = '0.5rem',
    // overlay
    overlayPosition = 'bottom',
    overlayBg       = 'var(--color-main)',
    overlayOpacity  = 70,
    overlayPaddingX = '8',
    overlayPaddingY = '8',
    // name
    showName        = true,
    nameInOverlay   = true,
    nameFontSize    = { pc: '1.125rem', mobile: '1rem' },
    nameFontWeight  = '600',
    nameColor       = '#ffffff',
    nameAlign       = 'left',
    // type label
    showTypeLabel        = true,
    typeLabelInOverlay   = true,
    typeLabelFontSize    = '0.8rem',
    typeLabelFontWeight  = '400',
    typeLabelColor       = '#ffffff',
    typeLabelAlign       = 'left',
    typeLabels           = {},
    // extra fields
    extraFields          = [],
    extraFieldsPosition  = 'below',
    extraFieldsGap       = '6',
    extraFieldsPaddingX  = '8',
    extraFieldsPaddingY  = '8',
    // card
    cardBg           = '#ffffff',
    cardRadius        = '0.5rem',
    cardShadow        = 'sm',
    cardBorder        = false,
    cardBorderColor   = '#e5e7eb',
    // link
    linkTarget        = '_self',
    wrapEntireCard    = true,
  } = config

  // ── Type label text — composer overrides win, then built-in dict ───────────
  const typeText = project.newDevelopment
    ? (typeLabels?.newDevelopment?.[locale]?.trim() || typeLabels?.newDevelopment?.en?.trim() || d.newDevelopment)
    : (typeLabels?.project?.[locale]?.trim()        || typeLabels?.project?.en?.trim()        || d.project)

  const namePc     = typeof nameFontSize === 'object' ? (nameFontSize.pc     ?? '1.125rem') : nameFontSize
  const nameMobile = typeof nameFontSize === 'object' ? (nameFontSize.mobile ?? '1rem')     : nameFontSize

  const href = `/${project.newDevelopment ? 'new-developments' : 'projects'}/${project.slug}`

  // ── Overlay uses two layers so content stays at full opacity ───────────────
  const overlayBgLayer = {
    background: resolveColor(overlayBg, 'var(--color-main)'),
    opacity:    overlayOpacity / 100,
    position:   'absolute', left: 0, right: 0,
    [overlayPosition === 'top' ? 'top' : 'bottom']: 0,
    // height is determined by the content layer below via matching positioning
    bottom: overlayPosition === 'top' ? undefined : 0,
    top:    overlayPosition === 'top' ? 0          : undefined,
  }
  const overlayContentLayer = {
    position: 'absolute', left: 0, right: 0,
    [overlayPosition === 'top' ? 'top' : 'bottom']: 0,
    padding: `${overlayPaddingY}px ${overlayPaddingX}px`,
  }

  const cardStyle = {
    background:   resolveColor(cardBg, '#ffffff'),
    borderRadius: cardRadius,
    border:       cardBorder ? `1px solid ${resolveColor(cardBorderColor, '#e5e7eb')}` : 'none',
    overflow:     'hidden',
  }

  const extraFieldsToRender = extraFields.filter(f => {
    const raw = project[f.key]
    return formatFieldValue(f.key, raw, d) !== null
  })

  const hasOverlay = showImage && overlayPosition !== 'none'

  // When there is no image (or no overlay), "inOverlay" flags are meaningless —
  // treat them as if the content should render below instead.
  const effectiveNameInOverlay      = nameInOverlay      && hasOverlay
  const effectiveTypeLabelInOverlay = typeLabelInOverlay && hasOverlay
  const effectiveExtraPos           = (extraFieldsPosition === 'overlay_bottom' && !hasOverlay)
                                        ? 'below'
                                        : extraFieldsPosition

  const hasOverlayCt = hasOverlay && (
    (showName      && effectiveNameInOverlay) ||
    (showTypeLabel && effectiveTypeLabelInOverlay) ||
    (effectiveExtraPos === 'overlay_bottom' && extraFieldsToRender.length > 0)
  )
  const hasBelow = (
    (showName      && !effectiveNameInOverlay) ||
    (showTypeLabel && !effectiveTypeLabelInOverlay) ||
    (effectiveExtraPos === 'below' && extraFieldsToRender.length > 0)
  )

  // ── Card content ───────────────────────────────────────────────────────────
  const cardContent = (
    <div style={cardStyle} className={SHADOW_CLASS[cardShadow] ?? ''}>

      {/* Image + overlay */}
      {showImage && (
        <div
          className={`relative w-full ${RATIO_CLASS[imageRatio] ?? 'aspect-[4/3]'} overflow-hidden`}
          style={{ borderRadius: imageRadius }}
        >
          <Image
            className="object-cover"
            src={project.cover ?? '/images/test.jpg'}
            fill
            alt={`Image of ${project.name}`}
            sizes="(max-width: 768px) 100vw, 50vw"
          />

          {hasOverlayCt && (
            <>
              {/* Semi-transparent background — separate so text keeps full opacity */}
              <div style={overlayBgLayer} aria-hidden="true" />

              {/* Content at full opacity */}
              <div style={overlayContentLayer}>
                {showTypeLabel && effectiveTypeLabelInOverlay && (
                  <p style={{ fontSize: typeLabelFontSize, fontWeight: typeLabelFontWeight, color: typeLabelColor, textAlign: typeLabelAlign, margin: '0 0 2px' }}>
                    {typeText}
                  </p>
                )}
                {showName && effectiveNameInOverlay && (
                  <h3 style={{ fontSize: `clamp(${nameMobile}, 2.5vw, ${namePc})`, fontWeight: nameFontWeight, color: nameColor, textAlign: nameAlign, margin: 0 }}>
                    {project.name}
                  </h3>
                )}
                {effectiveExtraPos === 'overlay_bottom' && extraFieldsToRender.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: `${extraFieldsGap}px`, marginTop: 4 }}>
                    {extraFieldsToRender.map(field => (
                      <ExtraField key={field.id} field={field} project={project} d={d} locale={locale} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Below-image content */}
      {hasBelow && (
        <div style={{ padding: `${extraFieldsPaddingY}px ${extraFieldsPaddingX}px`, background: resolveColor(cardBg, '#ffffff') }}>
          {showTypeLabel && !effectiveTypeLabelInOverlay && (
            <p style={{ fontSize: typeLabelFontSize, fontWeight: typeLabelFontWeight, color: resolveColor(typeLabelColor), textAlign: typeLabelAlign, margin: '0 0 2px' }}>
              {typeText}
            </p>
          )}
          {showName && !effectiveNameInOverlay && (
            <h3 style={{ fontSize: `clamp(${nameMobile}, 2.5vw, ${namePc})`, fontWeight: nameFontWeight, color: resolveColor(nameColor), textAlign: nameAlign, margin: extraFieldsToRender.length > 0 ? '0 0 6px' : '0' }}>
              {project.name}
            </h3>
          )}
          {effectiveExtraPos === 'below' && extraFieldsToRender.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: `${extraFieldsGap}px` }}>
              {extraFieldsToRender.map(field => (
                <ExtraField key={field.id} field={field} project={project} d={d} locale={locale} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )

  // ── Link wrapper ───────────────────────────────────────────────────────────
  const handleClick = () => sendGAEvent({
    event:        'select_project',
    project_id:   project.id,
    project_name: project.name,
    project_type: project.newDevelopment ? 'new_development' : 'project',
  })

  if (wrapEntireCard !== false) {
    return (
      <LinkComponent
        href={href}
        target={linkTarget}
        className="block w-full mt-2 hover:opacity-95 transition-opacity duration-150"
        onClick={handleClick}
      >
        {cardContent}
      </LinkComponent>
    )
  }

  return <div className="w-full mt-2">{cardContent}</div>
}

export default ProjectInfoCard